import React, { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from './AdminLayout.module.css';
import { BarChartIcon, TagIcon, BookIcon, DocumentIcon, FileIcon, HomeIcon, UserIcon, LanguageIcon, MicIcon, GlobeIcon, ImageIcon } from '../../components/Icons';

const menuGroups = [
  {
    key: 'overview',
    label: '概览',
    icon: <BarChartIcon size={18} />,
    items: [
      { path: '/admin/dashboard', label: '仪表板', icon: <BarChartIcon size={18} /> },
      { path: '/admin/novel-status', label: '状态检查', icon: <BarChartIcon size={18} /> },
    ],
  },
  {
    key: 'content',
    label: '内容管理',
    icon: <BookIcon size={18} />,
    items: [
      { path: '/admin/novels', label: '小说管理', icon: <BookIcon size={18} /> },
      { path: '/admin/volumes', label: '卷管理', icon: <DocumentIcon size={18} /> },
      { path: '/admin/chapters', label: '章节管理', icon: <FileIcon size={18} /> },
      { path: '/admin/chapter-content-edit', label: '中英文修改', icon: <LanguageIcon size={18} /> },
      { path: '/admin/categories', label: '分类管理', icon: <TagIcon size={18} /> },
      { path: '/admin/carousels', label: '轮播图管理', icon: <ImageIcon size={18} /> },
    ],
  },
  {
    key: 'ai',
    label: 'AI 工具',
    icon: <GlobeIcon size={18} />,
    items: [
      { path: '/admin/batch-pipeline', label: '一键处理', icon: <MicIcon size={18} /> },
      { path: '/admin/batch-all', label: '全量补全', icon: <MicIcon size={18} /> },
      { path: '/admin/batch-all-notes', label: '全量笔记', icon: <DocumentIcon size={18} /> },
      { path: '/admin/batch-text', label: '翻译+笔记', icon: <GlobeIcon size={18} /> },
      { path: '/admin/text-to-json', label: '文本转JSON', icon: <DocumentIcon size={18} /> },
      { path: '/admin/openai-translate', label: 'OpenAI 翻译', icon: <GlobeIcon size={18} /> },
      { path: '/admin/podcast-generation', label: '播客生成', icon: <LanguageIcon size={18} /> },
      { path: '/admin/openai-notes', label: 'AI 笔记生成', icon: <DocumentIcon size={18} /> },
    ],
  },
  {
    key: 'audio',
    label: '语音 & 音频',
    icon: <MicIcon size={18} />,
    items: [
      { path: '/admin/azure-speech', label: 'Azure 语音', icon: <LanguageIcon size={18} /> },
      { path: '/admin/edge-tts', label: 'Edge TTS', icon: <LanguageIcon size={18} /> },
      { path: '/admin/oss-mp3-combine', label: 'OSS 混音', icon: <FileIcon size={18} /> },
      { path: '/admin/speakers', label: '说话人管理', icon: <MicIcon size={18} /> },
    ],
  },
  {
    key: 'system',
    label: '系统管理',
    icon: <UserIcon size={18} />,
    items: [
      { path: '/admin/users', label: '用户管理', icon: <UserIcon size={18} /> },
      { path: '/admin/feedbacks', label: '用户反馈', icon: <DocumentIcon size={18} /> },
      { path: '/admin/vip-plans', label: 'VIP 套餐', icon: <TagIcon size={18} /> },
      { path: '/admin/payment-records', label: '支付记录', icon: <DocumentIcon size={18} /> },
    ],
  },
];

function AdminLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // 初始化：当前路径所在分组默认展开
  const getInitialOpen = useCallback(() => {
    const open = {};
    for (const group of menuGroups) {
      const isActive = group.items.some((item) => location.pathname === item.path);
      open[group.key] = isActive;
    }
    // 如果没有任何分组匹配，默认展开第一个
    if (!Object.values(open).some(Boolean)) {
      open[menuGroups[0].key] = true;
    }
    return open;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [openGroups, setOpenGroups] = useState(getInitialOpen);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className={styles.adminLayout}>
      {/* 侧边栏 */}
      <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.logo}>
            {sidebarCollapsed ? <BookIcon size={18} /> : <><BookIcon size={18} /> 管理</>}
          </h2>
          <button
            className={styles.toggleBtn}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className={styles.sidebarNav}>
          {menuGroups.map((group) => {
            const isOpen = openGroups[group.key];
            const hasActive = group.items.some(
              (item) => location.pathname === item.path
            );

            return (
              <div key={group.key} className={styles.navGroup}>
                <button
                  type="button"
                  className={`${styles.navGroupHeader} ${hasActive ? styles.navGroupActive : ''}`}
                  onClick={() => toggleGroup(group.key)}
                  title={sidebarCollapsed ? group.label : undefined}
                >
                  <span className={styles.navIcon}>{group.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className={styles.navGroupLabel}>{group.label}</span>
                      <span className={`${styles.navGroupArrow} ${isOpen ? styles.navGroupArrowOpen : ''}`}>
                        ›
                      </span>
                    </>
                  )}
                </button>

                {isOpen && !sidebarCollapsed && (
                  <div className={styles.navGroupItems}>
                    {group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`${styles.navItem} ${
                          location.pathname === item.path ? styles.active : ''
                        }`}
                      >
                        <span className={styles.navItemDot}>·</span>
                        <span className={styles.navLabel}>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          <Link to="/" className={styles.backToSite}>
            <span className={styles.navIcon}><HomeIcon size={18} /></span>
            {!sidebarCollapsed && <span>返回网站</span>}
          </Link>
        </div>
      </aside>

      {/* 主内容区域 */}
      <div className={styles.mainContent}>
        {/* 顶部导航栏 */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <h1 className={styles.pageTitle}>管理员控制台</h1>
          </div>
          <div className={styles.topBarRight}>
            <div className={styles.userInfo}>
              <span className={styles.userName}>👋 {user?.username}</span>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                退出登录
              </button>
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className={styles.pageContent}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
